import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, Mail, Lock, User, Phone, Shield, CheckCircle, Loader2, Calendar, MapPin, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import ReCAPTCHA from 'react-google-recaptcha';
import { generateFiscalCode } from '../utils/fiscalCode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // Test key

export const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [step, setStep] = useState(1); // 1: form + email verification, 2: phone verification, 3: captcha
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    phone_prefix: '+39',
    date_of_birth: '',
    place_of_birth: '',
    gender: '',
    address_street: '',
    address_number: '',
    address_cap: '',
    address_city: '',
    fiscal_code: '',
    gdpr_consent: false,
    role: 'patient'
  });
  const [otpCode, setOtpCode] = useState('');
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  
  // Prefissi telefonici europei supportati
  const europeanPrefixes = [
    { code: '+39', country: 'Italia', flag: '🇮🇹' },
    { code: '+33', country: 'Francia', flag: '🇫🇷' },
    { code: '+34', country: 'Spagna', flag: '🇪🇸' },
    { code: '+49', country: 'Germania', flag: '🇩🇪' },
    { code: '+44', country: 'Regno Unito', flag: '🇬🇧' },
    { code: '+31', country: 'Paesi Bassi', flag: '🇳🇱' },
    { code: '+32', country: 'Belgio', flag: '🇧🇪' },
    { code: '+41', country: 'Svizzera', flag: '🇨🇭' },
    { code: '+43', country: 'Austria', flag: '🇦🇹' },
    { code: '+351', country: 'Portogallo', flag: '🇵🇹' },
    { code: '+48', country: 'Polonia', flag: '🇵🇱' },
    { code: '+30', country: 'Grecia', flag: '🇬🇷' },
    { code: '+353', country: 'Irlanda', flag: '🇮🇪' },
    { code: '+45', country: 'Danimarca', flag: '🇩🇰' },
    { code: '+46', country: 'Svezia', flag: '🇸🇪' },
    { code: '+47', country: 'Norvegia', flag: '🇳🇴' },
    { code: '+358', country: 'Finlandia', flag: '🇫🇮' },
    { code: '+420', country: 'Rep. Ceca', flag: '🇨🇿' },
    { code: '+36', country: 'Ungheria', flag: '🇭🇺' },
    { code: '+40', country: 'Romania', flag: '🇷🇴' },
  ];
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  // Auto-genera codice fiscale quando cambiano i dati necessari
  useEffect(() => {
    if (!isLogin && formData.first_name && formData.last_name && formData.date_of_birth && formData.gender && formData.place_of_birth) {
      const cf = generateFiscalCode(
        formData.first_name,
        formData.last_name,
        formData.date_of_birth,
        formData.gender,
        formData.place_of_birth
      );
      if (cf && cf !== formData.fiscal_code) {
        setFormData(prev => ({ ...prev, fiscal_code: cf }));
      }
    }
  }, [formData.first_name, formData.last_name, formData.date_of_birth, formData.gender, formData.place_of_birth, isLogin]);

  // Email verification functions
  const sendEmailOTP = async () => {
    if (!formData.email || !formData.email.includes('@')) {
      toast.error('Inserisci un\'email valida');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-email-otp`, { email: formData.email });
      toast.success('Codice OTP inviato alla tua email');
      setEmailOtpSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio email OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailOTP = async () => {
    if (!emailOtpCode || emailOtpCode.length !== 6) {
      toast.error('Inserisci il codice a 6 cifre');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-email-otp`, { 
        email: formData.email, 
        code: emailOtpCode 
      });
      toast.success('Email verificata!');
      setEmailVerified(true);
      setStep(2); // Move to phone verification
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice errato');
    } finally {
      setLoading(false);
    }
  };

  // Ottiene il numero completo con prefisso
  const getFullPhoneNumber = () => {
    const phone = formData.phone.replace(/\s/g, '').replace(/^0+/, '');
    return `${formData.phone_prefix}${phone}`;
  };

  const sendOTP = async () => {
    if (!formData.phone || formData.phone.replace(/\s/g, '').length < 6) {
      toast.error('Inserisci un numero di telefono valido');
      return;
    }

    const fullPhone = getFullPhoneNumber();
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp`, { phone: fullPhone });
      toast.success('Codice OTP inviato al tuo telefono');
      setOtpSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Inserisci il codice a 6 cifre');
      return;
    }

    const fullPhone = getFullPhoneNumber();

    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-otp`, { 
        phone: fullPhone, 
        code: otpCode 
      });
      toast.success('Numero verificato!');
      setPhoneVerified(true);
      setStep(3); // Move to captcha step
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice errato');
    } finally {
      setLoading(false);
    }
  };

  const handleRecaptcha = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLogin) {
      // Direct login
      setLoading(true);
      try {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        toast.success('Accesso effettuato!');
        
        const role = response.data.user.role;
        if (role === 'patient') navigate('/dashboard/patient');
        else if (role === 'doctor' || role === 'ortottista') navigate('/dashboard/doctor');
        else if (role === 'admin') navigate('/dashboard/admin');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Credenziali non valide');
      } finally {
        setLoading(false);
      }
    } else {
      // Registration flow
      if (step === 1) {
        // Validate basic info and move to phone verification
        if (!formData.first_name || !formData.last_name || !formData.email || !formData.password) {
          toast.error('Compila tutti i campi obbligatori');
          return;
        }
        if (!formData.date_of_birth) {
          toast.error('La data di nascita è obbligatoria');
          return;
        }
        if (!formData.place_of_birth) {
          toast.error('Il luogo di nascita è obbligatorio');
          return;
        }
        if (!formData.gender) {
          toast.error('Il sesso è obbligatorio');
          return;
        }
        if (!formData.address_street || !formData.address_number || !formData.address_cap || !formData.address_city) {
          toast.error('L\'indirizzo di residenza completo è obbligatorio');
          return;
        }
        if (!formData.gdpr_consent) {
          toast.error('Devi accettare il regolamento e l\'informativa privacy');
          return;
        }
        if (formData.password.length < 6) {
          toast.error('La password deve essere di almeno 6 caratteri');
          return;
        }
        if (!formData.phone) {
          toast.error('Il numero di telefono è obbligatorio');
          return;
        }
        setStep(2);
        return;
      }
      
      if (step === 2 && !phoneVerified) {
        toast.error('Verifica prima il numero di telefono');
        return;
      }
      
      if (step === 3) {
        // Final registration with reCAPTCHA
        if (!recaptchaToken) {
          toast.error('Completa la verifica reCAPTCHA');
          return;
        }
        
        setLoading(true);
        try {
          const registrationData = {
            ...formData,
            phone: getFullPhoneNumber(), // Usa il numero completo con prefisso
            recaptcha_token: recaptchaToken
          };
          delete registrationData.phone_prefix; // Non serve nel backend
          
          const response = await axios.post(`${API}/auth/register`, registrationData);
          
          localStorage.setItem('token', response.data.access_token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          
          toast.success('Registrazione completata!');
          navigate('/dashboard/patient');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Errore registrazione');
          // Reset captcha on error
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
          setRecaptchaToken(null);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const resetForm = () => {
    setStep(1);
    setOtpCode('');
    setPhoneVerified(false);
    setOtpSent(false);
    setRecaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  // Google Login - REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 relative bg-white">
      {/* Background */}
      <div className="absolute inset-0 z-0" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}} />
      
      {/* Pulsante X per chiudere/tornare indietro */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-6 right-6 p-3 rounded-full bg-white hover:bg-gray-50 shadow-[0_4px_20px_rgba(0,0,0,0.05)] transition-all hover:scale-105 z-10 border border-gray-100"
        data-testid="auth-close-btn"
        aria-label="Chiudi"
      >
        <X className="w-5 h-5 text-gray-500" />
      </button>
      
      <div className="max-w-md w-full relative z-10">
        {/* Logo - Luxury */}
        <div className="text-center mb-10">
          <div className="flex flex-col items-center justify-center mb-6">
            <img 
              src="/logo-primevision-full.png" 
              alt="PRIME VISION" 
              className="h-64 w-auto object-contain"
            />
          </div>
          <h2 
            className="text-2xl font-normal"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
          >
            {isLogin ? 'Bentornato' : 'Crea il tuo account'}
          </h2>
          {!isLogin && (
            <div className="flex justify-center gap-3 mt-6">
              {[1, 2, 3].map((s) => (
                <div 
                  key={s}
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    step >= s ? 'bg-[#2A9FCB] text-white shadow-lg' : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form */}
        <div className="rounded-2xl shadow-lg p-8" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
          {/* Google Login Button */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-3 mb-6 border-2 hover:bg-gray-50 transition-colors"
            style={{borderColor: '#E5E7EB', background: 'white'}}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span style={{color: '#333'}}>Continua con Google</span>
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gradient-to-br from-[#e8f4fd] via-[#f4f9fe] to-[#dcedf8] text-[#2A9FCB]/70">oppure</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="auth-form">
            
            {/* LOGIN FORM */}
            {isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                      style={{borderColor: '#E5E7EB'}}
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="w-full pl-12 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2"
                      style={{borderColor: '#E5E7EB'}}
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      data-testid="toggle-password-btn"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Password dimenticata link */}
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm font-medium hover:underline"
                    style={{color: '#2A9FCB'}}
                    data-testid="forgot-password-link"
                  >
                    Password dimenticata?
                  </button>
                </div>
              </>
            )}

            {/* REGISTRATION STEP 1: Basic Info */}
            {!isLogin && step === 1 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Nome *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                        className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                        style={{borderColor: '#E5E7EB'}}
                        required
                        data-testid="first-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Cognome *</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                      style={{borderColor: '#E5E7EB'}}
                      required
                      data-testid="last-name-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                      style={{borderColor: emailVerified ? '#10B981' : '#E5E7EB'}}
                      required
                      disabled={emailVerified}
                      data-testid="email-input"
                    />
                    {emailVerified && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">✓</span>
                    )}
                  </div>
                  
                  {/* Email OTP Section */}
                  {!emailVerified && (
                    <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                      {!emailOtpSent ? (
                        <button
                          type="button"
                          onClick={sendEmailOTP}
                          disabled={loading || !formData.email.includes('@')}
                          className="w-full py-2 rounded-lg font-medium text-white flex items-center justify-center gap-2 text-sm"
                          style={{background: '#2A9FCB'}}
                          data-testid="send-email-otp-btn"
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                          Invia Codice Email
                        </button>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-xs text-blue-700">Codice inviato a {formData.email}</p>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Codice 6 cifre"
                              value={emailOtpCode}
                              onChange={(e) => setEmailOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              className="flex-1 px-3 py-2 border rounded-lg text-center text-lg tracking-widest"
                              maxLength={6}
                              data-testid="email-otp-input"
                            />
                            <button
                              type="button"
                              onClick={verifyEmailOTP}
                              disabled={loading || emailOtpCode.length !== 6}
                              className="px-4 py-2 rounded-lg font-medium text-white"
                              style={{background: '#10B981'}}
                              data-testid="verify-email-otp-btn"
                            >
                              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verifica'}
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => { setEmailOtpSent(false); setEmailOtpCode(''); }}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Reinvia codice
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="w-full pl-12 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2"
                      style={{borderColor: '#E5E7EB'}}
                      required
                      minLength={6}
                      placeholder="Minimo 6 caratteri"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      data-testid="toggle-password-reg-btn"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Telefono *</label>
                  <div className="flex gap-2">
                    <select
                      value={formData.phone_prefix}
                      onChange={(e) => setFormData({...formData, phone_prefix: e.target.value})}
                      className="w-28 py-3 px-2 border rounded-lg focus:outline-none focus:ring-2 text-sm"
                      style={{borderColor: '#E5E7EB'}}
                      data-testid="phone-prefix-select"
                    >
                      {europeanPrefixes.map(p => (
                        <option key={p.code} value={p.code}>
                          {p.flag} {p.code}
                        </option>
                      ))}
                    </select>
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                      <input
                        type="tel"
                        placeholder="333 123 4567"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value.replace(/[^\d\s]/g, '')})}
                        className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                        style={{borderColor: '#E5E7EB'}}
                        required
                        data-testid="phone-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-[#2A9FCB]/70 mt-1">Riceverai un SMS per verificare il numero (solo prefissi europei)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Data di Nascita *</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                      style={{borderColor: '#E5E7EB'}}
                      required
                      max={new Date().toISOString().split('T')[0]}
                      data-testid="date-of-birth-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Luogo di Nascita *</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <input
                      type="text"
                      placeholder="Es. Roma, Milano, Napoli..."
                      value={formData.place_of_birth}
                      onChange={(e) => setFormData({...formData, place_of_birth: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                      style={{borderColor: '#E5E7EB'}}
                      required
                      data-testid="place-of-birth-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Sesso *</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <select
                      value={formData.gender}
                      onChange={(e) => setFormData({...formData, gender: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 appearance-none bg-white"
                      style={{borderColor: '#E5E7EB'}}
                      required
                      data-testid="gender-select"
                    >
                      <option value="">Seleziona...</option>
                      <option value="M">Uomo</option>
                      <option value="F">Donna</option>
                      <option value="X">Preferisco non dichiararlo</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Etnia/Razza</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <select
                      value={formData.ethnicity || ''}
                      onChange={(e) => setFormData({...formData, ethnicity: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 appearance-none bg-white"
                      style={{borderColor: '#E5E7EB'}}
                      data-testid="ethnicity-select"
                    >
                      <option value="">Seleziona (opzionale)...</option>
                      <option value="caucasica">Caucasica</option>
                      <option value="afro">Africana/Afroamericana</option>
                      <option value="asiatica">Asiatica</option>
                      <option value="ispanica">Ispanica/Latina</option>
                      <option value="mediorientale">Mediorientale</option>
                      <option value="nativo_americana">Nativo Americana</option>
                      <option value="pacifica">Oceanica/Pacifica</option>
                      <option value="mista">Mista</option>
                      <option value="altro">Altro</option>
                    </select>
                  </div>
                </div>

                {/* Codice Fiscale (auto-generato) */}
                <div className="border-t pt-4 mt-4">
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>
                    Codice Fiscale
                    <span className="ml-2 text-xs text-gray-500">(generato automaticamente)</span>
                  </label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <input
                      type="text"
                      value={formData.fiscal_code}
                      onChange={(e) => setFormData({...formData, fiscal_code: e.target.value.toUpperCase()})}
                      className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 font-mono tracking-wider bg-gray-50"
                      style={{borderColor: '#E5E7EB'}}
                      placeholder="Inserisci i dati sopra per generare"
                      maxLength={16}
                      data-testid="fiscal-code-input"
                    />
                  </div>
                  {formData.fiscal_code && formData.fiscal_code.includes('XXXX') && (
                    <p className="text-xs text-amber-600 mt-1">
                      ⚠️ Codice comune non trovato. Puoi inserire il CF manualmente.
                    </p>
                  )}
                </div>

                {/* Indirizzo di Residenza */}
                <div className="border-t pt-4 mt-4">
                  <label className="block text-sm font-semibold mb-3" style={{color: '#2A9FCB'}}>Indirizzo di Residenza *</label>
                  
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="col-span-2">
                      <label className="block text-xs mb-1" style={{color: '#2A9FCB'}}>Via/Piazza</label>
                      <input
                        type="text"
                        placeholder="Es. Via Roma"
                        value={formData.address_street}
                        onChange={(e) => setFormData({...formData, address_street: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm"
                        style={{borderColor: '#E5E7EB'}}
                        required
                        data-testid="address-street-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{color: '#2A9FCB'}}>N°</label>
                      <input
                        type="text"
                        placeholder="Es. 10"
                        value={formData.address_number}
                        onChange={(e) => setFormData({...formData, address_number: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm"
                        style={{borderColor: '#E5E7EB'}}
                        required
                        data-testid="address-number-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs mb-1" style={{color: '#2A9FCB'}}>CAP</label>
                      <input
                        type="text"
                        placeholder="Es. 00100"
                        value={formData.address_cap}
                        onChange={(e) => setFormData({...formData, address_cap: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm"
                        style={{borderColor: '#E5E7EB'}}
                        maxLength="5"
                        required
                        data-testid="address-cap-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs mb-1" style={{color: '#2A9FCB'}}>Città</label>
                      <input
                        type="text"
                        placeholder="Es. Roma"
                        value={formData.address_city}
                        onChange={(e) => setFormData({...formData, address_city: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm"
                        style={{borderColor: '#E5E7EB'}}
                        required
                        data-testid="address-city-input"
                      />
                    </div>
                  </div>
                </div>

                {/* GDPR Consent Checkbox */}
                <div className="mt-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.gdpr_consent || false}
                      onChange={(e) => setFormData({...formData, gdpr_consent: e.target.checked})}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-[#2A9FCB] focus:ring-[#2A9FCB]"
                      required
                      data-testid="gdpr-consent-checkbox"
                    />
                    <span className="text-sm text-[#2A9FCB]/80">
                      Cliccando qui dichiaro di aver letto ed accettato il{' '}
                      <a 
                        href="/privacy" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#2A9FCB] hover:underline font-medium"
                      >
                        regolamento
                      </a>{' '}
                      e l'informativa ai sensi dell'art. 13 del GDPR 2016/679 e autorizzo il trattamento dei miei dati personali per finalità di marketing del titolare *
                    </span>
                  </label>
                </div>
              </>
            )}

            {/* REGISTRATION STEP 2: Phone Verification */}
            {!isLogin && step === 2 && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg" style={{background: '#ECFDF5', border: '1px solid #D1FAE5'}}>
                  <div className="flex items-center gap-2 mb-2">
                    <Phone className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">Verifica Numero di Telefono</span>
                  </div>
                  <p className="text-sm text-green-700">
                    {phoneVerified 
                      ? `✅ Numero ${getFullPhoneNumber()} verificato` 
                      : `Invieremo un SMS al numero ${getFullPhoneNumber()}`
                    }
                  </p>
                </div>

                {!phoneVerified && (
                  <>
                    {!otpSent ? (
                      <button
                        type="button"
                        onClick={sendOTP}
                        disabled={loading}
                        className="w-full py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2"
                        style={{background: '#2A9FCB'}}
                        data-testid="send-otp-btn"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Phone className="w-5 h-5" />
                            Invia Codice SMS
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>
                            Inserisci il codice a 6 cifre
                          </label>
                          <input
                            type="text"
                            maxLength={6}
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                            className="w-full px-4 py-3 text-center text-2xl tracking-widest border rounded-lg focus:outline-none focus:ring-2"
                            style={{borderColor: '#E5E7EB'}}
                            placeholder="000000"
                            data-testid="otp-input"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={verifyOTP}
                          disabled={loading || otpCode.length !== 6}
                          className="w-full py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50"
                          style={{background: '#2A9FCB'}}
                          data-testid="verify-otp-btn"
                        >
                          {loading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-5 h-5" />
                              Verifica Codice
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={sendOTP}
                          disabled={loading}
                          className="w-full py-2 text-sm text-[#2A9FCB]/80 hover:text-[#2A9FCB]"
                        >
                          Non hai ricevuto il codice? Reinvia
                        </button>
                      </div>
                    )}
                  </>
                )}

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full py-2 text-sm text-[#2A9FCB]/70 hover:text-[#2A9FCB]"
                >
                  ← Torna indietro
                </button>
              </div>
            )}

            {/* REGISTRATION STEP 3: reCAPTCHA */}
            {!isLogin && step === 3 && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg" style={{background: '#ECFDF5', border: '1px solid #D1FAE5'}}>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">Numero verificato!</span>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-5 h-5 text-[#2A9FCB]/80" />
                    <span className="font-medium text-[#2A9FCB]">Verifica di Sicurezza</span>
                  </div>
                  <div className="flex justify-center">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptcha}
                      hl="it"
                    />
                  </div>
                </div>

                {recaptchaToken && (
                  <div className="p-3 rounded-lg" style={{background: '#ECFDF5', border: '1px solid #D1FAE5'}}>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-green-700">Verifica completata</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Submit Button */}
            <button 
              type="submit" 
              disabled={loading || (!isLogin && step === 3 && !recaptchaToken)}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50" 
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isLogin ? (
                'Accedi'
              ) : step === 1 ? (
                'Continua'
              ) : step === 2 ? (
                phoneVerified ? 'Continua' : 'Verifica il numero'
              ) : (
                'Completa Registrazione'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                resetForm();
              }}
              className="text-sm hover:underline"
              style={{color: '#2A9FCB'}}
              data-testid="toggle-auth-mode"
            >
              {isLogin ? 'Non hai un account? Registrati' : 'Hai già un account? Accedi'}
            </button>
          </div>
        </div>

        {/* Info */}
        {!isLogin && (
          <p className="text-xs text-center text-[#2A9FCB]/70 mt-4">
            Registrandoti accetti i nostri Termini di Servizio e la Privacy Policy
          </p>
        )}
      </div>
    </div>
  );
};

export default Auth;
